<template>
	<div class="hotel-select-container fix-on-scroll">
		<div v-if="items.length" v-click-outside="closeHotelSelect" class="hotel-select" @click="toggleHotelSelect">
			<span @mouseenter="showHotelSelect = true"> {{ $t('hotelSelect') }}</span>
			<font-awesome-icon class="hotelicon" icon="fa-sharp fa-regular fa-hotel" size="1x" />
			<font-awesome-icon
				icon="fa-sharp fa-regular fa-chevron-down"
				size="1x"
				:class="{ rotated: showHotelSelect }"
			/>
		</div>

		<transition name="hotel-wrapper-fade">
			<div
				v-if="showHotelSelect"
				class="hotel-wrapper-container"
				:class="page && page.headingImage ? '' : 'noslider'"
				@mouseleave="closeHotelSelect"
			>
				<div ref="hotelWrapper" class="hotel-wrapper">
					<a href="https://wphotels.be" class="hotel-logo-container">
						<img :src="mainLogo" class="hotel-logo" loading="lazy" alt="alt" />
					</a>

					<a
						v-for="(item, index) in props.items.filter((i) => i.identifier !== currentHotel)"
						:key="index"
						:href="item.link"
						class="hotel-logo-container"
					>
						<img
							:src="getLogoSrc(item.identifier)"
							class="hotel-logo"
							loading="lazy"
							:alt="item.identifier"
						/>
					</a>
				</div>
			</div>
		</transition>
	</div>
</template>

<script setup>
import mainLogo from '@/assets/images/WP-main-logo.svg';

const { page } = usePage();
const currentHotel = '';

const props = defineProps({
	items: { type: Array, default: () => [] },
	slug: { type: String, default: '' },
});
const logoMap = { mainLogo };

const loadLogos = async (items) => {
	for (const item of items) {
		try {
			const module = await import(`@/assets/images/hotel-select-logos/${item.identifier}.svg`);
			logoMap[item.identifier] = module.default;
		} catch (e) {
			// eslint-disable-next-line no-console
			console.warn(`Logo not found for identifier: ${item.identifier}`, e);
		}
	}
};

const getLogoSrc = (identifier) => {
	return logoMap[identifier];
};

const showHotelSelect = ref(false);

const disableOpenOnHover = ref(false);

const hotelWrapper = ref(null);

const toggleHotelSelect = () => {
	showHotelSelect.value = !showHotelSelect.value;
	disableOpenOnHover.value = true;
};

const closeHotelSelect = () => {
	showHotelSelect.value = false;
};

const handleScroll = () => {
	const fixedElements = document.querySelectorAll('.fix-on-scroll');
	if (window.scrollY > 0) {
		fixedElements.forEach((el) => el.classList.add('scroll'));
	} else {
		fixedElements.forEach((el) => el.classList.remove('scroll'));
	}
};

onMounted(async () => {
	await nextTick();
	handleScroll();
	window.addEventListener('scroll', handleScroll);
	await loadLogos(props.items);
});

onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll);
});
</script>

<style lang="scss" scoped>
.hotel-wrapper-fade-enter-active,
.hotel-wrapper-fade-leave-active {
	transition:
		opacity 0.6s ease-in-out,
		transform 0.4s ease-out;
}

.hotel-wrapper-fade-enter-from,
.hotel-wrapper-fade-leave-to {
	opacity: 0;
	transform: translateY(-20px) scaleY(0.8);
}

.hotel-wrapper-fade-enter-to,
.hotel-wrapper-fade-leave-from {
	opacity: 1;
	transform: translateY(0) scaleY(1);
}

.rotated {
	transform: rotate(-180deg);
	transition: transform 0.4s ease-out;
}

.hotel-select-container {
	position: relative;

	svg {
		transition: transform 0.4s ease-out;
	}
}

.hotel-select {
	padding: 12px 50px;
	cursor: pointer;
	position: relative;
	text-transform: uppercase;
	display: flex;
	align-items: center;
	font-size: 14px;
	font-weight: 500;
	transition: opacity 0.3s ease-in-out;

	i {
		font-size: 12px;
	}

	svg {
		margin-left: 10px;
	}
}

.hotel-hover-in-area,
.hotel-hover-out-area {
	position: fixed;
	top: 105px;
	left: 0;
	right: 0;
	margin: auto;
	pointer-events: auto;
}

.hotel-hover-in-area.noslider,
.hotel-hover-out-area.noslider {
	top: 71px;
}

.hotel-hover-in-area {
	max-width: 75%;
	width: 100%;
	height: 40px;
	z-index: 1;
}

.hotel-hover-in-area.scroll,
.hotel-hover-out-area .scroll,
.hotel-select-container.scroll .hotel-hover-in-area,
.hotel-select-container.scroll .hotel-hover-out-area {
	top: 70px;
}

.hotel-hover-out-area {
	height: 130px;
	z-index: 0;
}

.hotel-hover-area::after {
	content: '';
	position: absolute;
	width: 100%;
	height: 100%;
	pointer-events: auto;
}

.hotel-wrapper-container {
	position: fixed;
	top: 104px;
	left: 3px;
	right: 3px;
	max-width: 1720px;
	display: flex;
	margin: auto;
	padding-left: 160px;
}

.hotel-wrapper {
	position: relative;
	margin-left: 35px;
	display: flex;
	height: 90px;
	margin-right: auto;
	text-align: center;
	color: #000;
	box-shadow: 0 18px 18px rgb(0 0 0 / 10%);
	z-index: 90;
	transition: all 0.3s ease-in-out;
	border-radius: 0 0 10px 10px;
	background: rgb(225 225 225 / 99%);
	overflow: hidden;
}

.hotel-wrapper-container.noslider {
	top: 70px;
}

.hotel-select-container.scroll .hotel-wrapper-container {
	top: 70px;
}

.hotel-wrapper.active,
.hotel-wrapper a {
	display: flex;
	flex-flow: column nowrap;
	align-items: center;
	place-content: space-between flex-start;
	color: #000;
	gap: 5px;
}

.hotel-wrapper a {
	width: 100%;
	flex-direction: row;
}

.hotel-logo-container {
	width: 100%;
	height: 100%;
	transition: all 0.3s ease-in-out;

	img,
	picture {
		height: 100%;
		max-width: unset;
		vertical-align: top;
	}

	&:hover {
		background: #fff;
	}

	&:first-child {
		width: 120%;
		padding: 0 30px 10px;
		position: relative;

		img:first-of-type,
		picture:first-of-type {
			max-width: 160px;
		}

		&::after {
			content: '';
			position: absolute;
			right: 0;
			top: 10px;
			bottom: 10px;
			width: 1px;
			background-color: #0b1d40;
		}
	}
}

.hotel-logo {
	height: 100%;
	padding: 10px;
}

.hotel-select span {
	display: inline-block;
	letter-spacing: 2px;
}

.hotelicon {
	display: none;
	font-size: 1.125em;
}

@media (max-width: 1080px) {
	.hotel-select {
		padding: 15px;
	}

	.hotel-wrapper-container {
		padding-left: 120px;
	}
}

@media (max-width: 920px) {
	.hotel-select {
		span {
			display: none;
		}

		.hotelicon {
			display: inline-block;
		}
	}

	.hotel-wrapper-container {
		padding-left: 0;
		margin-left: 0;

		.hotel-wrapper {
			margin-left: 0;
		}
	}
}

@media (max-width: 1240px) {
	.hotel-select {
		padding: 5px;
	}

	.hotel-hover-in-area,
	.hotel-hover-out-area {
		display: none;
	}

	.hotel-wrapper-container {
		top: 94px;
		left: 0;
		right: 0;
		margin: 0;
	}

	.hotel-wrapper {
		position: absolute;
		display: flex;
		flex-wrap: wrap;
		top: 10px;
		margin: 0;
		height: auto;
		border-radius: 0;
		text-align: center;
		color: #000;
		box-shadow: 0 18px 18px rgb(0 0 0 / 10%);
		z-index: 90;
		transition: all 0.3s ease-in-out;
		justify-content: center;
		background: rgb(225 225 225 / 99%);
		padding: 40px;
		max-width: 900px;

		.hotel-logo-container {
			position: relative;
			display: flex;
			flex-direction: column;
			align-items: center;
			width: 30%;
			height: auto;
			padding: 25px;
			justify-content: center;
			box-sizing: border-box;

			&:first-child {
				width: 80%;
				margin-bottom: 20px;
				position: relative;
			}

			&:first-child::after {
				content: '';
				position: relative;
				bottom: 0;
				left: 0;
				width: 100%;
				height: 1px;
				background-color: #0b1d40;
				transform: translateY(25px);
			}
		}
	}
}

@media screen and (max-width: 768px) and (orientation: landscape) {
	.hotel-logo {
		object-fit: contain;
		max-height: 60px;
		margin-top: 15px;
	}
}

@media (max-width: 600px) {
	.hotel-wrapper-fade-enter-from,
	.hotel-wrapper-fade-leave-to {
		transform: translateY(100svh);
	}

	.hotel-wrapper-fade-enter-to,
	.hotel-wrapper-fade-leave-from {
		transform: translateY(0);
	}

	.hotel-select-container.scroll .hotel-wrapper-container,
	.hotel-wrapper-container {
		top: 60px;

		.hotel-wrapper {
			.hotel-logo-container {
				width: 40%;

				&:first-child {
					width: 80%;
				}
			}
		}
	}
}

@media (max-width: 480px) {
	.hotel-wrapper {
		height: 100svh;
		width: 100%;
		padding-top: 0;

		.hotel-logo-container {
			width: 50%;
			height: auto;
			max-height: 130px;
			aspect-ratio: 1;
			padding: 5px;

			.hotel-logo {
				object-fit: contain;
				max-height: 100px;
			}

			&:first-child {
				width: 60%;
				margin-bottom: 15px;
				position: relative;
				aspect-ratio: unset;
			}

			&:first-child::after {
				content: '';
				position: relative;
				bottom: 0;
				left: 0;
				width: 180%;
				height: 1px;
				background-color: #0b1d40;
				transform: translateY(0);
				z-index: 50;
			}
		}
	}
}
</style>
